import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Rating,
  Popover,
  IconButton
} from '@mui/material';
import { supabase } from '../../lib/supabase';
import { fetchWorkers } from '../../services/workerService';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

interface PerformanceTabProps {
  farmId: string;
}

interface PerformanceReview {
  id: string;
  worker_id: string;
  review_date: string;
  score: number;
  comments?: string;
  reviewer: string;
  worker_name?: string;
  date?: string;
  review_period_start?: string;
  review_period_end?: string;
  created_at?: string;
}

interface Worker {
  id: string;
  name: string;
}

const PerformanceTab: React.FC<PerformanceTabProps> = ({ farmId }) => {
  const [reviews, setReviews] = useState<PerformanceReview[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<PerformanceReview | null>(null);
  const [editForm, setEditForm] = useState<any>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<null | HTMLElement>(null);
  const [popoverReview, setPopoverReview] = useState<PerformanceReview | null>(null);
  const [formData, setFormData] = useState({
    worker_id: '',
    review_date: new Date().toISOString().split('T')[0],
    review_period_start: '',
    review_period_end: '',
    score: 5,
    comments: '',
    reviewer: ''
  });

  useEffect(() => {
    loadData();
  }, [farmId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const workersData = await fetchWorkers(farmId);
      setWorkers(workersData);
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('worker_performance_reviews')
        .select(`*, workers!inner(name, farm_id)`)
        .eq('workers.farm_id', farmId)
        .order('date', { ascending: false });
      if (reviewsError) throw reviewsError;
      const formattedReviews = (reviewsData || []).map((review: any) => ({
        ...review,
        worker_name: review.workers?.name || 'Unknown',
      }));
      setReviews(formattedReviews);
    } catch (err) {
      setError('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddReview = async () => {
    try {
      setError(null);
      const { error } = await supabase
        .from('worker_performance_reviews')
        .insert([
          {
            worker_id: formData.worker_id,
            farm_id: farmId,
            date: formData.review_date,
            review_period_start: formData.review_period_start || null,
            review_period_end: formData.review_period_end || null,
            score: formData.score,
            comments: formData.comments,
            reviewer: formData.reviewer,
          },
        ]);
      if (error) throw error;
      setDialogOpen(false);
      setFormData({
        worker_id: '',
        review_date: new Date().toISOString().split('T')[0],
        review_period_start: '',
        review_period_end: '',
        score: 5,
        comments: '',
        reviewer: '',
      });
      loadData();
    } catch (err) {
      setError('Failed to add performance review');
    }
  };

  const handleEditClick = (review: PerformanceReview) => {
    setEditForm({ ...review });
    setSelectedReview(review);
    setEditDialogOpen(true);
  };

  const handleEditFormChange = (field: string, value: any) => {
    setEditForm((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleEditReviewSubmit = async () => {
    try {
      if (!selectedReview) return;
      const { error } = await supabase
        .from('worker_performance_reviews')
        .update({
          worker_id: editForm.worker_id,
          date: editForm.date || editForm.review_date,
          review_period_start: editForm.review_period_start || null,
          review_period_end: editForm.review_period_end || null,
          score: editForm.score,
          comments: editForm.comments,
          reviewer: editForm.reviewer,
        })
        .eq('id', selectedReview.id)
        .eq('farm_id', farmId);
      if (error) throw error;
      setEditDialogOpen(false);
      setSelectedReview(null);
      setEditForm(null);
      loadData();
    } catch (err) {
      setError('Failed to update performance review');
    }
  };

  const handleDeleteClick = (review: PerformanceReview) => {
    setSelectedReview(review);
    setDeleteDialogOpen(true);
  };

  const handleDeleteReview = async () => {
    try {
      if (!selectedReview) return;
      const { error } = await supabase
        .from('worker_performance_reviews')
        .delete()
        .eq('id', selectedReview.id)
        .eq('farm_id', farmId);
      if (error) throw error;
      setDeleteDialogOpen(false);
      setSelectedReview(null);
      loadData();
    } catch (err) {
      setError('Failed to delete performance review');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Performance Reviews</Typography>
        <Button variant="contained" onClick={() => setDialogOpen(true)}>
          Add Review
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Worker</TableCell>
              <TableCell>Review Date</TableCell>
              <TableCell>Score</TableCell>
              <TableCell>Reviewer</TableCell>
              <TableCell>Comments</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reviews.map((review) => (
              <TableRow
                key={review.id}
                hover
                style={{ cursor: 'pointer' }}
                onClick={e => {
                  setPopoverAnchor(e.currentTarget);
                  setPopoverReview(review);
                }}
              >
                <TableCell>{review.worker_name}</TableCell>
                <TableCell>{review.date ? new Date(review.date).toLocaleDateString() : '-'}</TableCell>
                <TableCell>
                  <Rating value={Number(review.score)} readOnly size="small" />
                  <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                    ({review.score}/5)
                  </Typography>
                </TableCell>
                <TableCell>{review.reviewer}</TableCell>
                <TableCell>{review.comments || '-'}</TableCell>
                <TableCell>
                  <IconButton onClick={e => { e.stopPropagation(); handleEditClick(review); }} size="small"><EditIcon /></IconButton>
                  <IconButton onClick={e => { e.stopPropagation(); handleDeleteClick(review); }} size="small"><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={() => { setPopoverAnchor(null); setPopoverReview(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 350, maxWidth: 500 }}>
          {popoverReview && (
            <>
              <Typography variant="h6" gutterBottom>{popoverReview.worker_name}</Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>{popoverReview.comments}</Typography>
              <Typography variant="body2"><b>Score:</b> {popoverReview.score}/5</Typography>
              <Typography variant="body2"><b>Reviewer:</b> {popoverReview.reviewer}</Typography>
              <Typography variant="body2"><b>Review Date:</b> {popoverReview.date ? new Date(popoverReview.date).toLocaleDateString() : '-'}</Typography>
              <Typography variant="body2"><b>Review Period:</b> {popoverReview.review_period_start || '-'} to {popoverReview.review_period_end || '-'}</Typography>
              <Typography variant="body2"><b>Created At:</b> {popoverReview.created_at ? new Date(popoverReview.created_at).toLocaleString() : '-'}</Typography>
            </>
          )}
        </Box>
      </Popover>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Performance Review</DialogTitle>
        <DialogContent>
          {editForm && (
            <Box sx={{ pt: 1 }}>
              <TextField
                label="Review Date"
                type="date"
                fullWidth
                value={editForm.date || editForm.review_date}
                onChange={e => handleEditFormChange('date', e.target.value)}
                margin="normal"
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Review Period Start"
                type="date"
                fullWidth
                value={editForm.review_period_start || ''}
                onChange={e => handleEditFormChange('review_period_start', e.target.value)}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Review Period End"
                type="date"
                fullWidth
                value={editForm.review_period_end || ''}
                onChange={e => handleEditFormChange('review_period_end', e.target.value)}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography component="legend">Performance Score</Typography>
                <Rating
                  value={Number(editForm.score)}
                  onChange={(_, newValue) => handleEditFormChange('score', newValue || 1)}
                  size="large"
                />
              </Box>
              <TextField
                label="Reviewer"
                fullWidth
                value={editForm.reviewer}
                onChange={e => handleEditFormChange('reviewer', e.target.value)}
                margin="normal"
                required
              />
              <TextField
                label="Comments"
                fullWidth
                multiline
                rows={4}
                value={editForm.comments}
                onChange={e => handleEditFormChange('comments', e.target.value)}
                margin="normal"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditReviewSubmit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Performance Review</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this performance review?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteReview}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Performance Review</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              select
              label="Worker"
              fullWidth
              value={formData.worker_id}
              onChange={(e) => setFormData({ ...formData, worker_id: e.target.value })}
              margin="normal"
              required
            >
              {workers.map((worker) => (
                <MenuItem key={worker.id} value={worker.id}>
                  {worker.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Review Date"
              type="date"
              fullWidth
              value={formData.review_date}
              onChange={(e) => setFormData({ ...formData, review_date: e.target.value })}
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Review Period Start"
              type="date"
              fullWidth
              value={formData.review_period_start}
              onChange={(e) => setFormData({ ...formData, review_period_start: e.target.value })}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Review Period End"
              type="date"
              fullWidth
              value={formData.review_period_end}
              onChange={(e) => setFormData({ ...formData, review_period_end: e.target.value })}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography component="legend">Performance Score</Typography>
              <Rating
                value={formData.score}
                onChange={(_, newValue) => setFormData({ ...formData, score: newValue || 1 })}
                size="large"
              />
            </Box>
            <TextField
              label="Reviewer"
              fullWidth
              value={formData.reviewer}
              onChange={(e) => setFormData({ ...formData, reviewer: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              label="Comments"
              fullWidth
              multiline
              rows={4}
              value={formData.comments}
              onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddReview} variant="contained">
            Add Review
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PerformanceTab;